import { RawDraftContentState } from 'draft-js'
import { ChannelKind, ChannelNotificationLevel } from '../interfaces/channels'
import { MentionType } from '../interfaces/mentions'
import { isGroupChannel } from './channelKind'

/**
 * Everything both sides of the wire have to agree on when a channel message
 * arrives: what it *says* in one line, whether it may make a noise at all, and
 * which OS notification tag it carries.
 *
 * The three used to live in separate files, but they are the same conversation:
 * server push (services/channels/notifyChannelMessage.ts -> notificationSender)
 * and the client (redux/messageAlertsMiddleware.ts) each answer all three
 * questions about the same message, and each one drifted when it was written
 * twice. Keeping them together is what makes a disagreement obvious.
 */

// ---------------------------------------------------------------------------
// Preview — the one-line summary of a message
// ---------------------------------------------------------------------------

/**
 * Used wherever a message has to be represented by a single line of text rather
 * than rendered: the sidebar's last-message preview, the push notification body,
 * and the in-tab desktop notification. Those three drifted apart once already —
 * each had its own copy of the 140-char cap and its own idea of what to say about
 * a message that is only an attachment.
 */

/** Long enough to be useful in a notification body, short enough not to be a wall. */
export const CHANNEL_PREVIEW_MAX_CHARS = 140

/**
 * One quote line for a reply: the parent's text at preview length, elided.
 *
 * Shared because it is built twice — the server stamps it onto the sent message,
 * and the composer shows the same line while the reply is still being written.
 * Two copies would let the bar and the quote disagree about where text stops.
 *
 * Not `buildMessagePreview`: that one has no ellipsis and falls back to
 * describing an attachment, neither of which a quote wants.
 */
export const buildReplyExcerpt = (text: string): string => {
  const trimmed = text.trim()
  return trimmed.length > CHANNEL_PREVIEW_MAX_CHARS
    ? `${trimmed.slice(0, CHANNEL_PREVIEW_MAX_CHARS)}…`
    : trimmed
}

/** Text blocks a two-line quote can show; anything past them is clipped anyway. */
export const REPLY_QUOTE_MAX_BLOCKS = 2

/**
 * The parent's rich body cut to what a reply quote draws: its first text blocks,
 * without pictures or blank lines, and an entity map pruned to match. Null when no
 * text is left. Built on both sides for the same reason as `buildReplyExcerpt`.
 */
export const buildReplyQuoteBody = (
  contentState: RawDraftContentState | null | undefined,
): RawDraftContentState | null => {
  if (!contentState?.blocks?.length) return null

  const blocks = contentState.blocks
    .filter((block) => block.type !== 'atomic' && !!block.text?.trim())
    .slice(0, REPLY_QUOTE_MAX_BLOCKS)
  if (!blocks.length) return null

  const entityKeys = new Set(
    blocks.flatMap((block) => (block.entityRanges ?? []).map((range) => String(range.key))),
  )
  const entityMap = Object.fromEntries(
    Object.entries(contentState.entityMap ?? {}).filter(([key]) => entityKeys.has(key)),
  )
  return { blocks, entityMap }
}

/** The minimum a caller has to supply — satisfied by both the entity and the client view type. */
export interface IPreviewableMessage {
  content: string | null
  attachments?: Array<{ name?: string | null }> | null
}

export const buildMessagePreview = (
  message: IPreviewableMessage,
  /** What to say when there is neither text nor an attachment to describe. */
  emptyFallback = '',
): string => {
  const text = (message.content ?? '').trim()
  if (text) return text.slice(0, CHANNEL_PREVIEW_MAX_CHARS)

  const attachments = message.attachments ?? []
  if (attachments.length === 1) return `shared ${attachments[0]?.name ?? 'a file'}`
  if (attachments.length > 1) return `shared ${attachments.length} files`
  return emptyFallback
}

// ---------------------------------------------------------------------------
// Gate — the per-conversation volume rules
// ---------------------------------------------------------------------------

/**
 * Shared by everything that decides whether to make a noise about a message.
 *
 * The server applies them before queueing push and the client applies them before
 * playing a sound or raising a desktop notification. They were written twice and
 * immediately disagreed about what counts as "directed", which meant a reply to
 * your own message pushed from the server while the client stayed silent.
 *
 * Only the rules that depend purely on the member's settings live here. Presence,
 * read cursors and tab visibility are deliberately left to each caller — they have
 * genuinely different information about those.
 */

/** Is the mute window still open? */
export const isChannelMuted = (mutedUntil: string | Date | null | undefined): boolean => {
  if (!mutedUntil) return false
  const until = mutedUntil instanceof Date ? mutedUntil : new Date(mutedUntil)
  return until.getTime() > Date.now()
}

export interface IChannelVolumeSettings {
  notificationLevel: ChannelNotificationLevel
  mutedUntil: string | Date | null
  /** A thread's own row: unmuted while its group is muted, so it hears every message. */
  overridesGroupMute?: boolean
}

/** Silenced outright — a mute window still open, or the level turned to nothing. */
export const isConversationMuted = (settings: IChannelVolumeSettings): boolean =>
  isChannelMuted(settings.mutedUntil) ||
  settings.notificationLevel === ChannelNotificationLevel.None

/**
 * A Threads row the viewer has silenced, by its own mute or its group's. Muted rows
 * carry no badge, count toward no total and are listed in their own section.
 */
export const isThreadRowMuted = (
  row: IChannelVolumeSettings & { groupSettings: IChannelVolumeSettings | null },
): boolean =>
  isConversationMuted(row) ||
  (!!row.groupSettings && isConversationMuted(row.groupSettings) && !row.overridesGroupMute)

type IThreadRowUnread = Parameters<typeof isThreadRowMuted>[0] & { unreadCount: number }

/** What a Threads row adds to the unread total: nothing while it is muted. */
export const countedUnread = (row: IThreadRowUnread): number =>
  isThreadRowMuted(row) ? 0 : row.unreadCount

/** Unread across Threads rows, leaving out muted ones — a section heading's rollup. */
export const sumThreadRowsUnread = (rows: ReadonlyArray<IThreadRowUnread>): number =>
  rows.reduce((total, row) => total + countedUnread(row), 0)

interface IMentionLike {
  id: string
  type?: MentionType | string | null
  broadcast?: readonly string[] | null
  teams?: readonly string[] | null
  typed?: boolean | null
}

/**
 * Was this user @-mentioned by name? Someone reached only by `@here`/`@channel` or a
 * team chip was not: a personal @ gets through any mute and unmutes the conversation.
 */
export const isPersonallyMentioned = (
  mentions: ReadonlyArray<IMentionLike> | null | undefined,
  userId: string,
): boolean =>
  (mentions ?? []).some(
    (mention) =>
      mention.id === userId &&
      (!mention.type || mention.type === MentionType.InternalUser) &&
      (!!mention.typed || (!mention.broadcast?.length && !mention.teams?.length)),
  )

/**
 * True when this member should hear nothing about the message.
 *
 * `isDirected` means the message was aimed at them — an @-mention, or a reply to
 * something they wrote. Directed messages beat the channel's volume setting but not
 * a mute; only a personal @-mention beats that.
 */
export const isSilencedByChannelSettings = (
  settings: IChannelVolumeSettings | null | undefined,
  isDirected: boolean,
  isPersonalMention = false,
): boolean => {
  if (!settings || isPersonalMention) return false
  if (isChannelMuted(settings.mutedUntil)) return true
  if (settings.notificationLevel === ChannelNotificationLevel.None) return true
  return !isDirected && settings.notificationLevel !== ChannelNotificationLevel.All
}

/**
 * The level a new membership starts on. A group starts on Engagement; a thread's
 * own row is only its mute toggle, so it starts unmuted and defers to the group.
 */
export const defaultNotificationLevel = (channel: {
  kind: ChannelKind
  parentChannelId: string | null
}): ChannelNotificationLevel =>
  isGroupChannel(channel) ? ChannelNotificationLevel.Engaged : ChannelNotificationLevel.All

/** A Primary member hears every message in every one of the group's threads. */
export const isPrimaryInGroup = (
  groupSettings: IChannelVolumeSettings | null | undefined,
): boolean =>
  !!groupSettings &&
  groupSettings.notificationLevel === ChannelNotificationLevel.All &&
  !isConversationMuted(groupSettings)

/** A thread the reader hears in full: Primary in its group, or unmuted on its own under a muted one. */
export const hearsEveryThreadMessage = (
  groupSettings: IChannelVolumeSettings | null | undefined,
  overridesGroupMute: boolean,
): boolean =>
  isPrimaryInGroup(groupSettings) ||
  (overridesGroupMute && !!groupSettings && isConversationMuted(groupSettings))

/**
 * True when a member should hear nothing about a message in one of a group's threads.
 *
 * A personal @-mention always gets through. Otherwise either mute wins outright —
 * the thread's own, or the group's — and then the group's level decides: Primary
 * hears everything, and Engagement hears an @-mention or a thread they have posted in.
 */
export const isSilencedInGroupConversation = ({
  threadSettings,
  groupSettings,
  isMention,
  isPersonalMention = false,
  hasEngaged,
}: {
  threadSettings: IChannelVolumeSettings | null | undefined
  groupSettings: IChannelVolumeSettings | null | undefined
  isMention: boolean
  isPersonalMention?: boolean
  hasEngaged: boolean
}): boolean => {
  if (isPersonalMention) return false
  if (threadSettings && isConversationMuted(threadSettings)) return true
  if (groupSettings && isConversationMuted(groupSettings)) {
    // Unmuted on its own under a muted group: the member picked this thread out.
    return !threadSettings?.overridesGroupMute
  }
  if (isMention || isPrimaryInGroup(groupSettings)) return false
  if (groupSettings?.notificationLevel === ChannelNotificationLevel.Mentions) return true
  return !hasEngaged
}

// ---------------------------------------------------------------------------
// Tag — how two banners for one message collapse into one
// ---------------------------------------------------------------------------

/**
 * The tag is what decides whether two notifications collapse into one banner or
 * stack as two, and two independent paths raise notifications for the same
 * message: server push (notificationSender -> webSocketQueue -> service-worker.js)
 * when the recipient's app is closed, and the client directly
 * (redux/messageAlertsMiddleware.ts) when their tab is open but not looking. Those
 * paths overlap for a *directed* message — notifyChannelMessage only suppresses
 * push for `!isDirected && isOnline`, so a mention or a reply to your own message
 * still pushes to someone sitting on a backgrounded tab.
 *
 * Both sides therefore have to produce a byte-identical string. They were written
 * separately and did not: the client omitted the recipient suffix and the mention
 * prefix, so a mentioned user with a backgrounded tab got two banners for one
 * message — the exact thing the tags exist to prevent. Hence one function.
 *
 * Scoped by recipient because push tags are per-subscription and a shared device
 * should not let one person's banner replace another's.
 */
export const buildChannelNotificationTag = ({
  channelId,
  recipientUserId,
  isMention,
}: {
  channelId: string
  recipientUserId: string
  isMention: boolean
}): string =>
  isMention
    ? `channel-mention-${channelId}-${recipientUserId}`
    : `channel-${channelId}-${recipientUserId}`
